import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';

/**
 * SwipeToCloseDirective
 * 
 * Adds swipe-to-close gesture support for drawers on mobile devices.
 * Applies to nz-drawer or any container that should close on swipe.
 * 
 * Usage:
 * ```html
 * <nz-drawer
 *   appSwipeToClose
 *   (swipeClosed)="closeDrawer()"
 *   [swipeDirection]="'right'"
 * >
 * ```
 */
@Directive({
  selector: '[appSwipeToClose]',
  standalone: true,
})
export class SwipeToCloseDirective implements OnInit, OnDestroy {
  private el = inject(ElementRef);
  private ngZone = inject(NgZone);

  /** Direction to swipe for closing: 'left', 'right', or 'down' */
  @Input() swipeDirection: 'left' | 'right' | 'down' = 'right';

  /** Minimum distance (px) to trigger close */
  @Input() swipeThreshold = 80;

  /** Minimum velocity (px/ms) for fast swipe to trigger close */
  @Input() swipeVelocityThreshold = 0.3;

  /** Maximum screen width (px) to enable swipe gesture */
  @Input() swipeMaxWidth = 768;

  /** Emits when swipe gesture completes and drawer should close */
  @Output() swipeClosed = new EventEmitter<void>();

  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private currentTranslateX = 0;
  private currentTranslateY = 0;
  private isSwiping = false;
  private drawerContent: HTMLElement | null = null;

  private boundTouchStart = this.onTouchStart.bind(this);
  private boundTouchMove = this.onTouchMove.bind(this);
  private boundTouchEnd = this.onTouchEnd.bind(this);

  ngOnInit(): void {
    // Run outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      // Wait for drawer to render, then attach listeners
      setTimeout(() => {
        this.attachListeners();
      }, 100);
    });
  }

  ngOnDestroy(): void {
    this.detachListeners();
  }

  private attachListeners(): void {
    // Find the drawer content wrapper
    const element = this.el.nativeElement as HTMLElement;
    
    // NgZorro drawer structure: nz-drawer > .ant-drawer > .ant-drawer-content-wrapper > .ant-drawer-content > .ant-drawer-body
    this.drawerContent = element.querySelector('.ant-drawer-content-wrapper') || 
                         element.querySelector('.ant-drawer-body') ||
                         element;

    if (this.drawerContent) {
      this.drawerContent.addEventListener('touchstart', this.boundTouchStart, { passive: true });
      this.drawerContent.addEventListener('touchmove', this.boundTouchMove, { passive: false });
      this.drawerContent.addEventListener('touchend', this.boundTouchEnd, { passive: true });
    }
  }

  private detachListeners(): void {
    if (this.drawerContent) {
      this.drawerContent.removeEventListener('touchstart', this.boundTouchStart);
      this.drawerContent.removeEventListener('touchmove', this.boundTouchMove);
      this.drawerContent.removeEventListener('touchend', this.boundTouchEnd);
    }
  }

  private isMobile(): boolean {
    return window.innerWidth <= this.swipeMaxWidth;
  }

  private onTouchStart(event: TouchEvent): void {
    if (!this.isMobile()) return;

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.currentTranslateX = 0;
    this.currentTranslateY = 0;
    this.isSwiping = false;
  }

  private onTouchMove(event: TouchEvent): void {
    if (!this.isMobile()) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    // Determine if this is a horizontal or vertical gesture
    if (!this.isSwiping) {
      // Only start swiping if horizontal movement is dominant
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        // Check direction matches expected swipe
        const isCorrectDirection = 
          (this.swipeDirection === 'right' && deltaX > 0) ||
          (this.swipeDirection === 'left' && deltaX < 0) ||
          (this.swipeDirection === 'down' && deltaY > 0);

        if (isCorrectDirection) {
          this.isSwiping = true;
        }
      }
    }

    if (this.isSwiping) {
      // Prevent vertical scroll while swiping horizontally
      event.preventDefault();

      this.currentTranslateX = deltaX;
      this.currentTranslateY = deltaY;

      // Apply visual feedback
      this.applySwipeTransform(deltaX, deltaY);
    }
  }

  private onTouchEnd(): void {
    if (!this.isMobile() || !this.isSwiping) {
      this.resetTransform();
      return;
    }

    const deltaTime = Date.now() - this.touchStartTime;
    const velocity = Math.abs(this.currentTranslateX) / deltaTime;

    // Check if swipe should close
    const passedThreshold = Math.abs(this.currentTranslateX) > this.swipeThreshold;
    const fastSwipe = velocity > this.swipeVelocityThreshold && Math.abs(this.currentTranslateX) > 30;

    if (passedThreshold || fastSwipe) {
      // Animate out then emit close
      this.animateClose();
    } else {
      // Snap back
      this.resetTransform();
    }

    this.isSwiping = false;
  }

  private applySwipeTransform(deltaX: number, deltaY: number): void {
    if (!this.drawerContent) return;

    let transform = '';
    let opacity = 1;

    if (this.swipeDirection === 'right' || this.swipeDirection === 'left') {
      // Only allow movement in the correct direction
      const clampedX = this.swipeDirection === 'right' 
        ? Math.max(0, deltaX) 
        : Math.min(0, deltaX);
      
      transform = `translateX(${clampedX}px)`;
      opacity = 1 - (Math.abs(clampedX) / 300) * 0.3; // Subtle fade
    } else if (this.swipeDirection === 'down') {
      const clampedY = Math.max(0, deltaY);
      transform = `translateY(${clampedY}px)`;
      opacity = 1 - (Math.abs(clampedY) / 300) * 0.3;
    }

    this.drawerContent.style.transition = 'none';
    this.drawerContent.style.transform = transform;
    this.drawerContent.style.opacity = String(Math.max(0.7, opacity));
  }

  private animateClose(): void {
    if (!this.drawerContent) {
      this.emitClose();
      return;
    }

    // Animate out of view
    let finalTransform = '';
    if (this.swipeDirection === 'right') {
      finalTransform = 'translateX(100%)';
    } else if (this.swipeDirection === 'left') {
      finalTransform = 'translateX(-100%)';
    } else if (this.swipeDirection === 'down') {
      finalTransform = 'translateY(100%)';
    }

    this.drawerContent.style.transition = 'transform 200ms ease-out, opacity 200ms ease-out';
    this.drawerContent.style.transform = finalTransform;
    this.drawerContent.style.opacity = '0';

    // Emit close after animation
    setTimeout(() => {
      this.emitClose();
      this.resetTransform();
    }, 200);
  }

  private resetTransform(): void {
    if (!this.drawerContent) return;

    this.drawerContent.style.transition = 'transform 200ms ease-out, opacity 200ms ease-out';
    this.drawerContent.style.transform = '';
    this.drawerContent.style.opacity = '';

    // Clean up after transition
    setTimeout(() => {
      if (this.drawerContent) {
        this.drawerContent.style.transition = '';
      }
    }, 200);
  }

  private emitClose(): void {
    this.ngZone.run(() => {
      this.swipeClosed.emit();
    });
  }
}
